/**
 * Каталог мебели: предметы на полу — центр в точке плана, поворот, габариты из каталога.
 * `elevation` у напольного предмета — на какой высоте стоит его низ: телевизор и приставка
 * стоят на тумбе, а не на полу. Кухня — гарнитур, холодильник, обеденный стол и стул;
 * кабинет — компьютерный стол и кресло. Перед у всех моделей кодом смотрит в +Z (к нижнему
 * краю символа), спинка стула и задняя стенка гарнитура — к верхнему.
 * Модели файлом (`.glb` из генератора) вписываются в размеры каталога, поэтому размеры
 * у них взяты по пропорциям самой модели — иначе её растянет. Настоящую мебель дальше даст
 * генератор шкафов.
 */
import { LayerKind } from "@/shared/plan/layerKind";
import { CatalogAttach, type CatalogSection } from "../../types";
import { FurnitureId } from "./ids";
import type { FurnitureCatalogItem } from "./types";

/**
 * Пределы размеров по жизни: кровать — под матрасы от 900 до 2000 мм плюс рама, диван —
 * от двухместного до большого. Они только расширяют общий коридор (`itemSizeRange`,
 * от четверти до четырёх размеров) там, где его не хватает, и не сужают его.
 */
const BED_LIMITS = {
	width: { min: 1000, max: 2200 },
	depth: { min: 1800, max: 2350 },
};
const SOFA_LIMITS = {
	width: { min: 1400, max: 3200 },
	depth: { min: 800, max: 1150 },
};
const COMPACT_SOFA_LIMITS = {
	width: { min: 1200, max: 2400 },
	depth: { min: 700, max: 1000 },
};
const CORNER_SOFA_LIMITS = {
	width: { min: 2000, max: 3600 },
	depth: { min: 1400, max: 2200 },
};
const ARMCHAIR_LIMITS = {
	width: { min: 600, max: 1100 },
	depth: { min: 650, max: 1000 },
};

/** Папка моделей повторяет первый сегмент id: `bed-oak` → `furniture/bed/`. */
const furnitureModelUrl = (id: string) => `/models/furniture/${id.split("-")[0]}/${id}.glb`;

/**
 * Предмет с моделью-файлом из `public/models/furniture`: габарит модели — габарит предмета.
 * По умолчанию ставится на пол; настенный (кондиционер, вытяжка) — `attach: OnlyWall`,
 * у него `elevation` — высота центра.
 */
const glb = (
	fields: Omit<FurnitureCatalogItem, "view3d" | "attach" | "elevation"> & {
		elevation?: number;
		attach?: CatalogAttach;
	},
	options: {
		turn?: number;
		tintable?: boolean;
		upholstery?: boolean;
		upholsteryColor?: string;
	} = {},
): FurnitureCatalogItem => ({
	...fields,
	elevation: fields.elevation ?? 0,
	attach: fields.attach ?? CatalogAttach.Everywhere,
	view3d: {
		type: "model",
		url: furnitureModelUrl(fields.id),
		width: fields.width,
		height: fields.height,
		depth: fields.depth,
		color: fields.color,
		...options,
	},
});

export const furnitureSection: CatalogSection<FurnitureCatalogItem> = {
	kind: LayerKind.Furniture,
	label: "Мебель",
	canChangeDimensions: true,
	subGroups: [
		"Спальня",
		"Гостиная",
		"Кухня",
		"Кабинет",
		"Хранение",
		"Детская",
		"Техника",
		"Спорт",
		"Балкон",
	],
	families: [
		{ id: "bed-double", name: "Кровать двуспальная", defaultItem: "bed-scandi" },
		{ id: "nightstand", name: "Тумба прикроватная", defaultItem: "nightstand" },
		{ id: "dresser", name: "Комод", defaultItem: "dresser" },
		{ id: "sofa", name: "Диван", defaultItem: "sofa" },
		{ id: "sofa-corner", name: "Диван угловой", defaultItem: "sofa-corner-black" },
		{ id: "armchair", name: "Кресло", defaultItem: "armchair" },
		{ id: "dining-table", name: "Стол обеденный", defaultItem: "table-oak" },
		{ id: "chair", name: "Стул", defaultItem: "chair-wood" },
		{ id: "desk", name: "Стол письменный", defaultItem: "desk-computer" },
		{ id: "chair-office", name: "Кресло компьютерное", defaultItem: "chair-office" },
		{ id: "tv-stand", name: "Тумба под телевизор", defaultItem: "tv-stand-walnut" },
		{ id: "console", name: "Игровая приставка", defaultItem: "playstation" },
		{ id: "turntable", name: "Проигрыватель винила", defaultItem: "turntable-cabinet" },
		{ id: "fridge", name: "Холодильник", defaultItem: "fridge" },
		{ id: "wardrobe", name: "Шкаф платяной", defaultItem: "wardrobe" },
		{ id: "bookshelf", name: "Стеллаж", defaultItem: "shelf-cube" },
		{ id: "floor-lamp", name: "Торшер", defaultItem: "floor-lamp" },
		{ id: "coat-stand", name: "Вешалка-стойка", defaultItem: "coat-stand" },
	],
	items: [
		{
			id: FurnitureId.Sofa,
			family: "sofa",
			sizeLimits: SOFA_LIMITS,
			subGroup: "Гостиная",
			name: "Диван современный",
			description:
				"Трёхместный диван 2200 × 900 на деревянных ножках, мягкие подушки и округлые подлокотники.",
			width: 2200,
			height: 850,
			depth: 900,
			elevation: 0,
			color: "#c9a25a",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: furnitureModelUrl("sofa-modern"),
				width: 2200,
				height: 850,
				depth: 900,
				color: "#c9a25a",
				tintable: true,
				upholstery: true,
			},
		},
		{
			id: FurnitureId.SofaAccordion,
			family: "sofa",
			sizeLimits: SOFA_LIMITS,
			subGroup: "Гостиная",
			name: "Диван-аккордеон",
			description:
				"Раскладной трёхместный диван 2200 × 950 с механизмом «аккордеон» и прямыми подлокотниками.",
			width: 2200,
			height: 900,
			depth: 950,
			elevation: 0,
			color: "#9ca3af",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: furnitureModelUrl("sofa-accordion"),
				width: 2200,
				height: 900,
				depth: 950,
				color: "#9ca3af",
				tintable: true,
				upholstery: true,
			},
		},
		{
			id: FurnitureId.SofaCompact,
			family: "sofa",
			sizeLimits: COMPACT_SOFA_LIMITS,
			subGroup: "Гостиная",
			name: "Диван двухместный",
			description:
				"Компактный диван 1700 × 780 из букле на чёрных ножках — для кабинета или небольшой комнаты.",
			width: 1700,
			height: 680,
			depth: 780,
			elevation: 0,
			color: "#cfcac3",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: furnitureModelUrl("sofa-loveseat"),
				width: 1700,
				height: 680,
				depth: 780,
				color: "#cfcac3",
				tintable: true,
				upholstery: true,
			},
		},
		glb(
			{
				id: FurnitureId.Armchair,
				family: "armchair",
				sizeLimits: ARMCHAIR_LIMITS,
				subGroup: "Гостиная",
				name: "Кресло мягкое",
				description: "Мягкое кресло 800 × 780 с округлой спинкой на деревянных ножках.",
				width: 800,
				height: 850,
				depth: 780,
				color: "#d8cbb5",
			},
			{ tintable: true, upholstery: true },
		),
		glb(
			{
				id: FurnitureId.Beanbag,
				round: true,
				subGroup: "Гостиная",
				name: "Кресло-мешок",
				description: "Мягкое кресло-мешок ⌀ 800.",
				width: 800,
				height: 830,
				depth: 800,
				color: "#b9b2a7",
			},
			{ tintable: true, upholstery: true },
		),
		glb({
			id: FurnitureId.FloorLamp,
			family: "floor-lamp",
			round: true,
			subGroup: "Гостиная",
			name: "Торшер с абажуром",
			description: "Торшер высотой 1600 с тканевым абажуром на чёрной стойке. Декор: свет не даёт.",
			width: 700,
			height: 1600,
			depth: 700,
			color: "#e8e2d6",
		}),
		glb(
			{
				id: FurnitureId.BedSingle,
				family: "bed-double",
				sizeLimits: BED_LIMITS,
				subGroup: "Спальня",
				name: "Кровать с мягким изголовьем",
				description: "Кровать 1500 × 2000 с серым мягким изголовьем и пледом.",
				width: 1500,
				height: 850,
				depth: 2000,
				color: "#c8c6c2",
			},
			{ upholstery: true },
		),
		glb({
			id: FurnitureId.Nightstand,
			family: "nightstand",
			subGroup: "Спальня",
			name: "Тумба прикроватная, дуб",
			description: "Тумба 500 × 400 из дуба с ящиком и открытой полкой, высота 510.",
			width: 500,
			height: 510,
			depth: 400,
			color: "#c9a47c",
		}),
		glb({
			id: FurnitureId.TableRound,
			family: "dining-table",
			round: true,
			subGroup: "Кухня",
			name: "Стол круглый",
			description: "Круглый стол ⌀ 1200 из ореха на центральной опоре, высота 750.",
			width: 1200,
			height: 750,
			depth: 1200,
			color: "#7a5438",
		}),
		glb({
			id: FurnitureId.CoffeeMachine,
			subGroup: "Кухня",
			name: "Кофемашина",
			description: "Рожковая кофемашина 260 × 400, стоит на столешнице (низ на 900 мм).",
			width: 260,
			height: 370,
			depth: 400,
			elevation: 900,
			color: "#b8bcc2",
		}),
		glb({
			id: FurnitureId.ExerciseBike,
			subGroup: "Спорт",
			name: "Велотренажёр",
			description: "Велотренажёр 1200 × 520, высота 1260.",
			width: 1200,
			height: 1260,
			depth: 520,
			color: "#2b2d31",
		}),
		{
			id: FurnitureId.TvStand,
			family: "tv-stand",
			subGroup: "Гостиная",
			name: "Тумба под телевизор навесная",
			description:
				"Подвесная тумба 1600 × 400 на 150 мм от пола: фасады без ручек в матовом лаке, дубовый корпус. На неё встают телевизор и приставка.",
			width: 1600,
			height: 350,
			depth: 400,
			elevation: 150,
			color: "#f3f2ee",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "component",
				id: "tv-stand-floating",
				width: 1600,
				height: 350,
				depth: 400,
				color: "#f3f2ee",
				tintable: true,
			},
		},
		{
			id: FurnitureId.TvStandWalnut,
			family: "tv-stand",
			subGroup: "Гостиная",
			name: "Тумба под телевизор, орех",
			description:
				"Напольная тумба 1600 × 400 на тонких металлических ножках: фасады под орех, открытая ниша посередине. На неё встают телевизор и приставка.",
			width: 1600,
			height: 500,
			depth: 400,
			elevation: 0,
			color: "#8a5a3b",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: furnitureModelUrl("tv-stand-walnut"),
				width: 1600,
				height: 500,
				depth: 400,
				color: "#8a5a3b",
			},
		},
		{
			id: FurnitureId.KitchenSet,
			subGroup: "Кухня",
			name: "Кухонный гарнитур прямой",
			description:
				"Гарнитур 2400 мм: нижние шкафы со столешницей на 900 мм, мойка и варочная панель, верхние шкафы глубиной 350 до 2200 мм. На плане верхние шкафы — пунктиром.",
			width: 2400,
			height: 2200,
			depth: 600,
			elevation: 0,
			color: "#e9e5dc",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "component",
				id: "kitchen-set",
				width: 2400,
				height: 2200,
				depth: 600,
				color: "#e9e5dc",
				tintable: true,
			},
			// Кухни бывают от полутора до почти пяти метров, а глубина и высота — почти
			// типовые: столешница 600, верх шкафов от 2000 до потолка
			sizeLimits: {
				width: { min: 1200, max: 4800 },
				depth: { min: 500, max: 700 },
				height: { min: 2000, max: 2700 },
			},
		},
		{
			id: FurnitureId.Fridge,
			family: "fridge",
			subGroup: "Кухня",
			name: "Холодильник двухкамерный",
			description: "Отдельностоящий холодильник 600 × 650, высота 1850, морозильник снизу.",
			width: 600,
			height: 1850,
			depth: 650,
			elevation: 0,
			color: "#dfe3e6",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "component",
				id: "fridge",
				width: 600,
				height: 1850,
				depth: 650,
				color: "#dfe3e6",
			},
		},
		{
			id: "desk-computer",
			family: "desk",
			subGroup: "Кабинет",
			name: "Стол компьютерный",
			description:
				"Письменный стол 1400 × 700, высота 750: боковина слева, тумба с тремя ящиками справа.",
			width: 1400,
			height: 750,
			depth: 700,
			elevation: 0,
			color: "#e8e4dc",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "component",
				id: "desk-computer",
				width: 1400,
				height: 750,
				depth: 700,
				color: "#e8e4dc",
				tintable: true,
			},
		},
		{
			id: "chair-office",
			family: "chair-office",
			subGroup: "Кабинет",
			name: "Кресло офисное",
			description: "Кресло на колёсах с подлокотниками и высокой спинкой, габарит 620 × 620.",
			width: 620,
			height: 1100,
			depth: 620,
			elevation: 0,
			color: "#2b2f36",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "component",
				id: "chair-office",
				width: 620,
				height: 1100,
				depth: 620,
				color: "#2b2f36",
				tintable: true,
			},
		},
		// Техника на рабочем столе: низ на высоте столешницы, 750 мм. Id прежние, из декора, —
		// предметы в одном слое, сохранённые проекты открываются как были
		{
			id: "laptop",
			subGroup: "Техника",
			name: "Ноутбук",
			description: "Открытый ноутбук 14″ на столе.",
			width: 320,
			height: 220,
			depth: 280,
			elevation: 750,
			color: "#9aa1a9",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "component",
				id: "laptop",
				width: 320,
				height: 220,
				depth: 280,
				color: "#9aa1a9",
			},
		},
		{
			id: "monitor",
			subGroup: "Техника",
			name: "Монитор 27″",
			description: "Монитор 27″ на подставке, стоит на столе у стены.",
			width: 615,
			height: 470,
			depth: 200,
			elevation: 750,
			color: "#1f2329",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "component",
				id: "monitor",
				width: 615,
				height: 470,
				depth: 200,
				color: "#1f2329",
			},
		},
		{
			id: "tv",
			subGroup: "Техника",
			name: "Телевизор",
			description: "Панель 55″ на подставке, стоит на навесной тумбе.",
			width: 1200,
			height: 700,
			depth: 60,
			elevation: 500,
			color: "#1f2937",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "component",
				id: "tv",
				width: 1200,
				height: 760,
				depth: 220,
				color: "#1f2937",
			},
		},
		{
			id: "tv-wall",
			subGroup: "Техника",
			name: "Телевизор настенный",
			description: "Панель 55″ на кронштейне, вешается на стену. Центр экрана — на высоте 1300 мм.",
			width: 1230,
			height: 710,
			depth: 50,
			elevation: 1300,
			color: "#1f2937",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "tv-wall",
				width: 1230,
				height: 710,
				depth: 50,
				color: "#1f2937",
			},
		},
		{
			id: "playstation",
			family: "console",
			subGroup: "Техника",
			name: "PlayStation",
			description:
				"Игровая консоль 390 × 260, лежит на тумбе рядом с телевизором: белые боковые панели и чёрный корпус.",
			width: 390,
			height: 100,
			depth: 260,
			elevation: 500,
			color: "#f1f2f4",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "component",
				id: "playstation",
				width: 390,
				height: 100,
				depth: 260,
				color: "#f1f2f4",
			},
		},
		// Вторая партия моделей-файлов (03.10.2026). Все смотрят передом в +Z, к нижнему краю
		// символа, — разворачивать не нужно. Корпусная мебель (шкафы, комоды, стеллажи, столы
		// на прямых ножках) из генератора приходит с лишней глубиной или квадратной — её
		// размеры взяты по жизни: прямоугольный корпус растяжение переносит без искажений.
		// Мягкая мебель, кровати, растения — строго по пропорциям модели.
		glb(
			{
				id: "sofa-chesterfield",
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван честерфилд",
				description:
					"Классический трёхместный диван 2200 × 1010 из тёмно-коричневой кожи с каретной стяжкой.",
				width: 2200,
				height: 880,
				depth: 1010,
				color: "#5a3a2a",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: "sofa-midcentury",
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван кожаный, коньяк",
				description:
					"Трёхместный диван 2100 × 1010 в стиле середины века: кожа цвета коньяка, деревянная рама.",
				width: 2100,
				height: 900,
				depth: 1010,
				color: "#a0592e",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: "sofa-scandi",
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван скандинавский",
				description: "Трёхместный диван 2000 × 900 из светло-серой ткани на деревянном основании.",
				width: 2000,
				height: 900,
				depth: 900,
				color: "#b9b8b4",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: "sofa-rounded",
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван мягкий, бежевый",
				description: "Двухместный диван 1900 × 970 с пышными подушками, бежевая ткань.",
				width: 1900,
				height: 690,
				depth: 970,
				color: "#cbbca8",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: "sofa-dark",
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван графитовый",
				description: "Трёхместный диван 2100 × 1090 из графитовой ткани с широкими подлокотниками.",
				width: 2100,
				height: 820,
				depth: 1090,
				color: "#33363b",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: "sofa-olive",
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван оливковый",
				description: "Трёхместный диван 2200 × 1080 с оливковой обивкой и деревянными ножками.",
				width: 2200,
				height: 770,
				depth: 1080,
				color: "#64621d",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: "armchair-lounge",
				family: "armchair",
				sizeLimits: ARMCHAIR_LIMITS,
				subGroup: "Гостиная",
				name: "Кресло лаунж",
				description: "Кресло для отдыха 830 × 760: чёрная кожа в гнутой фанере, поворотная опора.",
				width: 830,
				height: 1020,
				depth: 760,
				color: "#2a2422",
			},
			{ upholstery: true },
		),
		glb({
			id: "armchair-papasan",
			family: "armchair",
			round: true,
			sizeLimits: ARMCHAIR_LIMITS,
			subGroup: "Гостиная",
			name: "Кресло-папасан",
			description: "Плетёное кресло-чаша ⌀ 1000 из ротанга с толстой подушкой.",
			width: 930,
			height: 560,
			depth: 1000,
			color: "#a67c4e",
		}),
		glb({
			id: "chair-gaming",
			family: "chair-office",
			subGroup: "Кабинет",
			name: "Кресло игровое",
			description: "Геймерское кресло 730 × 715 с высокой спинкой, подушками и красными вставками.",
			width: 730,
			height: 1300,
			depth: 715,
			color: "#1d1d20",
		}),
		glb({
			id: "bed-scandi",
			family: "bed-double",
			sizeLimits: BED_LIMITS,
			subGroup: "Спальня",
			name: "Кровать двуспальная, скандинавская",
			description: "Кровать 1970 × 2100 из светлого дерева с простым изголовьем и серым бельём.",
			width: 1970,
			height: 1050,
			depth: 2100,
			color: "#b98a5c",
		}),
		glb({
			id: "bed-wooden",
			family: "bed-double",
			sizeLimits: BED_LIMITS,
			subGroup: "Спальня",
			name: "Кровать двуспальная, дерево",
			description: "Деревянная кровать 1900 × 2150 с решётчатым изголовьем, высота 1270.",
			width: 1900,
			height: 1270,
			depth: 2150,
			color: "#8a5a35",
		}),
		glb({
			id: "dresser",
			family: "dresser",
			subGroup: "Спальня",
			name: "Комод ореховый",
			description: "Комод 930 × 480 из ореха на шесть ящиков, высота 1000.",
			width: 930,
			height: 1000,
			depth: 480,
			color: "#7a4e30",
		}),
		glb({
			id: "shelf-cube",
			family: "bookshelf",
			subGroup: "Хранение",
			name: "Стеллаж из ячеек",
			description: "Белый стеллаж 1200 × 390 на 12 квадратных ячеек, высота 1470.",
			width: 1200,
			height: 1470,
			depth: 390,
			color: "#e9e9e7",
		}),
		glb({
			id: "table-oak",
			family: "dining-table",
			subGroup: "Кухня",
			name: "Стол обеденный, дуб",
			description: "Массивный стол 1400 × 800 из дуба на прямых ножках, высота 750.",
			width: 1400,
			height: 750,
			depth: 800,
			color: "#8a5a36",
		}),
		glb({
			id: "table-side",
			round: true,
			subGroup: "Гостиная",
			name: "Столик приставной",
			description:
				"Круглый столик ⌀ 455 с дубовой столешницей на чёрном металлическом каркасе, высота 550.",
			width: 455,
			height: 550,
			depth: 450,
			color: "#8a5a36",
		}),
		glb({
			id: "chair-wood",
			family: "chair",
			subGroup: "Кухня",
			name: "Стул деревянный",
			description: "Стул 560 × 560 из массива с решётчатой спинкой, высота 900.",
			width: 560,
			height: 900,
			depth: 560,
			color: "#b08458",
		}),
		glb({
			id: "chair-plastic",
			family: "chair",
			subGroup: "Кухня",
			name: "Стул пластиковый",
			description: "Стул 445 × 535 с белым литым сиденьем на деревянных ножках, высота 820.",
			width: 445,
			height: 820,
			depth: 535,
			color: "#eeeeec",
		}),
		glb({
			id: "bar-stool",
			round: true,
			subGroup: "Кухня",
			name: "Стул барный",
			description: "Барный стул ⌀ 490 на металлическом каркасе с подножкой, сиденье на 750 мм.",
			width: 490,
			height: 750,
			depth: 490,
			color: "#3a3a3c",
		}),
		glb({
			id: "fridge-side",
			family: "fridge",
			subGroup: "Кухня",
			name: "Холодильник Side-by-Side",
			description: "Двухдверный холодильник 900 × 700 из нержавейки с диспенсером, высота 1800.",
			width: 900,
			height: 1800,
			depth: 700,
			color: "#b9bcc0",
		}),
		glb({
			id: "microwave",
			subGroup: "Кухня",
			name: "Микроволновка",
			description: "Микроволновая печь 500 × 410, стоит на столешнице (низ на 900 мм).",
			width: 500,
			height: 300,
			depth: 410,
			elevation: 900,
			color: "#5a5d61",
		}),
		glb({
			id: "desk-oak",
			family: "desk",
			subGroup: "Кабинет",
			name: "Стол письменный, дуб",
			description: "Стол 1100 × 600 из дуба с ящиком на металлических ножках-шпильках, высота 750.",
			width: 1100,
			height: 750,
			depth: 600,
			color: "#b98a52",
		}),
		glb({
			id: "desk-standing",
			family: "desk",
			subGroup: "Кабинет",
			name: "Стол с подъёмом",
			description:
				"Стол 1400 × 730 с электроподъёмом: столешница под орех на чёрных опорах, поднят до 1230.",
			width: 1400,
			height: 1230,
			depth: 730,
			color: "#6b4630",
		}),
		glb({
			id: "desk-gaming",
			family: "desk",
			subGroup: "Кабинет",
			name: "Стол игровой",
			description: "Стол 1500 × 735 с чёрной столешницей на Z-образных опорах, высота 735.",
			width: 1500,
			height: 735,
			depth: 735,
			color: "#1d1d20",
		}),
		glb({
			id: "gaming-setup",
			subGroup: "Кабинет",
			name: "Игровое место",
			description: "Стол 1600 × 815 в сборе: два монитора, системный блок, клавиатура, наушники.",
			width: 1600,
			height: 1280,
			depth: 815,
			color: "#1d1d20",
		}),
		{
			id: "gaming-pc",
			subGroup: "Техника",
			name: "Системный блок игровой",
			description:
				"Корпус 230 × 480 со стеклянной стенкой и RGB-вентиляторами, высота 500. Стоит на полу или на столе.",
			width: 230,
			height: 500,
			depth: 480,
			elevation: 0,
			color: "#141518",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "component",
				id: "gaming-pc",
				width: 230,
				height: 500,
				depth: 480,
				color: "#141518",
			},
		},
		glb({
			id: "console-stand",
			family: "console",
			subGroup: "Техника",
			name: "Игровая приставка вертикальная",
			description: "Приставка стоя и два геймпада, стоит на тумбе рядом с телевизором.",
			width: 440,
			height: 400,
			depth: 235,
			elevation: 500,
			color: "#eeeff1",
		}),
		glb({
			id: "turntable-cabinet",
			family: "turntable",
			subGroup: "Техника",
			name: "Проигрыватель винила на тумбе",
			description:
				"Тумба 1000 × 490 из ореха с отделением для пластинок, сверху проигрыватель. Высота 800.",
			width: 1000,
			height: 800,
			depth: 490,
			color: "#7a4e30",
		}),
		glb({
			id: "turntable",
			family: "turntable",
			subGroup: "Техника",
			name: "Проигрыватель винила настольный",
			description: "Проигрыватель 450 × 400 на ореховом основании, крышка открыта. Стоит на столе.",
			width: 450,
			height: 390,
			depth: 400,
			elevation: 750,
			color: "#6b4630",
		}),
		glb({
			id: "speaker-floor",
			subGroup: "Техника",
			name: "Колонка напольная",
			description: "Напольная акустика 375 × 375 в корпусе из ореха, высота 1100.",
			width: 375,
			height: 1100,
			depth: 375,
			color: "#6b4630",
		}),
		glb({
			id: "robot-vacuum",
			round: true,
			subGroup: "Техника",
			name: "Робот-пылесос",
			description: "Робот-пылесос ⌀ 350, высота 95.",
			width: 350,
			height: 95,
			depth: 350,
			color: "#26282b",
		}),
		glb({
			id: "floor-lamp-tripod",
			family: "floor-lamp",
			round: true,
			subGroup: "Гостиная",
			name: "Торшер на треноге",
			description:
				"Торшер высотой 1600 на деревянной треноге с тёмным абажуром. Декор: свет не даёт.",
			width: 610,
			height: 1600,
			depth: 620,
			color: "#3b2a20",
		}),
		glb({
			id: "changing-table",
			subGroup: "Детская",
			name: "Пеленальный комод",
			description: "Белый комод 900 × 685 с ящиками и пеленальным матрасиком, высота 810.",
			width: 900,
			height: 810,
			depth: 685,
			color: "#eeeeec",
		}),
		glb({
			id: "rocking-horse",
			subGroup: "Детская",
			name: "Лошадка-качалка",
			description: "Деревянная лошадка-качалка 370 × 900, высота 840.",
			width: 370,
			height: 840,
			depth: 900,
			color: "#b98a52",
		}),
		glb({
			id: "teepee",
			subGroup: "Детская",
			name: "Вигвам игровой",
			description: "Детский вигвам 1300 × 990 из светлой ткани на деревянных шестах, высота 1480.",
			width: 1300,
			height: 1480,
			depth: 990,
			color: "#dedad2",
		}),
		glb({
			id: "treadmill",
			subGroup: "Спорт",
			name: "Беговая дорожка",
			description: "Беговая дорожка 970 × 1340, пульт у дальнего края, высота 1450.",
			width: 970,
			height: 1450,
			depth: 1340,
			color: "#232427",
		}),
		glb({
			id: "punching-bag",
			subGroup: "Спорт",
			name: "Боксёрская груша на стойке",
			description: "Чёрная груша на напольной стойке 1430 × 640, высота 2200.",
			width: 1430,
			height: 2200,
			depth: 640,
			color: "#1f1f21",
		}),
		glb({
			id: "wall-bars",
			subGroup: "Спорт",
			name: "Шведская стенка",
			description:
				"Деревянная шведская стенка 1100 × 360 с турником, высота 2400. Ставится к стене.",
			width: 1100,
			height: 2400,
			depth: 360,
			color: "#b98a52",
		}),
		// Четвёртая партия (03.10.2026). Буфет и кровать с ящиками пришли боком — развёрнуты
		// `turn`, велосипед стоит вдоль стены. Односпальные кровати генератор делает почти
		// квадратными, поэтому они сужены до размеров настоящей односпальной.
		glb({
			id: "nightstand-white",
			family: "nightstand",
			subGroup: "Спальня",
			name: "Тумба прикроватная, белая",
			description: "Белая тумба 485 × 375 с двумя ящиками на дубовых ножках, высота 550.",
			width: 485,
			height: 550,
			depth: 375,
			color: "#e9e8e4",
		}),
		glb({
			id: "dresser-tall",
			family: "dresser",
			subGroup: "Спальня",
			name: "Комод высокий, белый",
			description: "Белый комод 640 × 445 на пять ящиков, высота 1200.",
			width: 640,
			height: 1200,
			depth: 445,
			color: "#e9e8e4",
		}),
		glb({
			id: "wardrobe-oak",
			family: "wardrobe",
			subGroup: "Хранение",
			name: "Шкаф платяной, дуб",
			description: "Двустворчатый шкаф 1600 × 600 из дуба, высота 2000.",
			width: 1600,
			height: 2000,
			depth: 600,
			color: "#a87b4a",
		}),
		glb({
			id: "clothes-rack",
			subGroup: "Хранение",
			name: "Вешалка напольная для одежды",
			description: "Стойка-рейл 1250 × 450 на чёрном каркасе с полкой для обуви, высота 1600.",
			width: 1250,
			height: 1600,
			depth: 450,
			color: "#2b2b2d",
		}),
		glb({
			id: "coat-stand",
			family: "coat-stand",
			round: true,
			subGroup: "Хранение",
			name: "Вешалка-стойка",
			description: "Деревянная вешалка на треноге с крючками наверху, высота 1750.",
			width: 600,
			height: 1750,
			depth: 600,
			color: "#6b2f1f",
		}),
		glb(
			{
				id: "sideboard",
				family: "tv-stand",
				subGroup: "Гостиная",
				name: "Буфет",
				description: "Низкий буфет 1800 × 450 из ореха на тонких ножках, высота 850.",
				width: 1800,
				height: 850,
				depth: 450,
				color: "#7a4e30",
			},
			// Модель смотрит фасадом в +X — разворачиваем к нижнему краю символа
			{ turn: 270 },
		),
		glb({
			id: "armchair-rocking",
			family: "armchair",
			sizeLimits: ARMCHAIR_LIMITS,
			subGroup: "Гостиная",
			name: "Кресло-качалка",
			description: "Деревянное кресло-качалка 620 × 755 с реечной спинкой, высота 1050.",
			width: 620,
			height: 1050,
			depth: 755,
			color: "#a0703f",
		}),
		glb({
			id: "floor-lamp-arc",
			family: "floor-lamp",
			subGroup: "Гостиная",
			name: "Торшер-дуга",
			description:
				"Торшер с дугой 1480 мм и плафоном-куполом на тяжёлом основании, высота 1800. Декор: свет не даёт.",
			width: 1480,
			height: 1800,
			depth: 430,
			color: "#3a3a3c",
		}),
		glb({
			id: "room-divider",
			subGroup: "Гостиная",
			name: "Ширма",
			description: "Складная ширма из четырёх створок с ротангом, 1600 × 1540.",
			width: 1600,
			height: 1540,
			depth: 400,
			color: "#8a5a36",
		}),
		glb({
			id: "chair-mesh",
			family: "chair-office",
			subGroup: "Кабинет",
			name: "Кресло эргономичное",
			description:
				"Кресло 730 × 670 с сетчатой спинкой, подголовником и регулируемыми подлокотниками.",
			width: 730,
			height: 1200,
			depth: 670,
			color: "#26282b",
		}),
		glb({
			id: "dishwasher",
			subGroup: "Кухня",
			name: "Посудомоечная машина",
			description: "Отдельностоящая посудомойка 600 × 600 из нержавейки, высота 850.",
			width: 600,
			height: 850,
			depth: 600,
			color: "#b9bcc0",
		}),
		glb({
			id: "range-hood",
			attach: CatalogAttach.OnlyWall,
			subGroup: "Кухня",
			name: "Вытяжка каминная",
			description:
				"Настенная вытяжка 720 × 370 из нержавейки. Центр — на 1950 мм, низ купола в 650 мм над плитой.",
			width: 720,
			height: 800,
			depth: 370,
			elevation: 1950,
			color: "#b9bcc0",
		}),
		glb({
			id: "trash-bin",
			subGroup: "Кухня",
			name: "Ведро с педалью",
			description: "Мусорное ведро 400 × 320 из нержавейки с педалью, высота 650.",
			width: 400,
			height: 650,
			depth: 320,
			color: "#b9bcc0",
		}),
		glb({
			id: "bench-back",
			subGroup: "Кухня",
			name: "Скамья со спинкой",
			description: "Деревянная скамья 1400 × 490 со спинкой — к обеденному столу, высота 920.",
			width: 1400,
			height: 920,
			depth: 490,
			color: "#8a5a36",
		}),
		glb({
			id: "air-conditioner",
			attach: CatalogAttach.OnlyWall,
			subGroup: "Техника",
			name: "Кондиционер",
			description:
				"Внутренний блок сплит-системы 900 × 300, вешается под потолок. Центр — на 2200 мм.",
			width: 900,
			height: 360,
			depth: 300,
			elevation: 2200,
			color: "#f1f2f3",
			powerOutlet: "socket-ac",
		}),
		glb({
			id: "soundbar",
			subGroup: "Техника",
			name: "Саундбар",
			description: "Узкая звуковая панель 900 × 150, лежит на тумбе перед телевизором.",
			width: 900,
			height: 80,
			depth: 150,
			elevation: 500,
			color: "#1d1d20",
		}),
		glb({
			id: "dumbbell-rack",
			subGroup: "Спорт",
			name: "Стойка с гантелями",
			description: "Двухъярусная стойка 800 × 335 с набором гантелей, высота 550.",
			width: 800,
			height: 550,
			depth: 335,
			color: "#232427",
		}),
		glb(
			{
				id: "bicycle",
				subGroup: "Спорт",
				name: "Велосипед",
				description: "Городской велосипед 1750 мм на подножке, тёмно-зелёная рама, кожаное седло.",
				width: 1750,
				height: 960,
				depth: 490,
				color: "#2f4a3a",
			},
			// Модель вытянута вдоль Z — ставим вдоль стены
			{ turn: 90 },
		),
		// Пятая партия. Мягкая мебель, кровати и стулья — строго по пропорциям модели,
		// корпусная — по жизни. Пришедшие боком или задом развёрнуты `turn` к нижнему краю
		// символа; зелёный стул пришёл повёрнутым на 45°.
		glb(
			{
				id: FurnitureId.SofaBeige,
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван низкий, бежевый",
				description: "Низкий широкий диван 2400 × 1010 из бежевой ткани.",
				width: 2400,
				height: 670,
				depth: 1010,
				color: "#9b8272",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.SofaBrown,
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван тёмно-коричневый",
				description: "Трёхместный диван 2200 × 990 в тёмно-коричневой обивке.",
				width: 2200,
				height: 835,
				depth: 990,
				color: "#392f29",
			},
			{ upholstery: true },
		),
		glb({
			id: FurnitureId.SofaLeather,
			family: "sofa",
			sizeLimits: SOFA_LIMITS,
			subGroup: "Гостиная",
			name: "Диван кожаный, тёмный",
			description: "Трёхместный диван 2300 × 1010 из тёмно-коричневой кожи.",
			width: 2300,
			height: 760,
			depth: 1010,
			color: "#5d3f2b",
		}),
		glb(
			{
				id: FurnitureId.SofaClassic,
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван классический, светлый",
				description: "Трёхместный диван 2300 × 965 в светлой ткани.",
				width: 2300,
				height: 805,
				depth: 965,
				color: "#b5b0a2",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.SofaCream,
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван карамельный",
				description: "Двухместный диван 2000 × 880 в карамельной обивке.",
				width: 2000,
				height: 860,
				depth: 880,
				color: "#ae8962",
			},
			{ upholstery: true, upholsteryColor: "#e6ca9c" },
		),
		glb(
			{
				id: FurnitureId.SofaFabric,
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван длинный, тканевый",
				description: "Длинный прямой диван 3000 × 900 из светлой ткани.",
				width: 3000,
				height: 780,
				depth: 900,
				color: "#aba18d",
			},
			// Модель смотрит передом в +X
			{ turn: 270, upholstery: true },
		),
		glb(
			{
				id: FurnitureId.SofaModular,
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван модульный, горчичный",
				description: "Модульный диван 2800 × 950 из горчичной ткани, низкая спинка.",
				width: 2800,
				height: 730,
				depth: 950,
				color: "#916c3d",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.SofaSectional,
				family: "sofa",
				sizeLimits: SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван большой с пуфом",
				description: "Широкий диван 3000 × 1380 с глубоким сиденьем и пуфом спереди.",
				width: 3000,
				height: 690,
				depth: 1380,
				color: "#968673",
			},
			// Модель смотрит передом в −X
			{ turn: 90, upholstery: true },
		),
		glb(
			{
				id: FurnitureId.SofaCornerBlack,
				family: "sofa-corner",
				sizeLimits: CORNER_SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван угловой, чёрный",
				description: "Угловой диван 2800 × 1570 с шезлонгом справа, чёрная обивка.",
				width: 2800,
				height: 870,
				depth: 1570,
				color: "#2a2c2b",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.SofaCornerBrown,
				family: "sofa-corner",
				sizeLimits: CORNER_SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван угловой, горчичный",
				description: "Угловой диван 3000 × 2190 со спинкой на обе стороны угла, угол слева.",
				width: 3000,
				height: 780,
				depth: 2190,
				color: "#916c40",
			},
			// Модель стоит спинкой к −X
			{ turn: 270, upholstery: true },
		),
		glb(
			{
				id: FurnitureId.SofaCornerCurved,
				family: "sofa-corner",
				sizeLimits: CORNER_SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван полукруглый",
				description: "Полукруглый диван 3200 × 1890 для зоны у камина или в центре комнаты.",
				width: 3200,
				height: 670,
				depth: 1890,
				color: "#a89a8b",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.SofaCornerLeather,
				family: "sofa-corner",
				sizeLimits: CORNER_SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван угловой, кожаный",
				description: "Угловой диван 2800 × 1625 из коричневой кожи, шезлонг слева.",
				width: 2800,
				height: 840,
				depth: 1625,
				color: "#7a513c",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.SofaCornerModular,
				family: "sofa-corner",
				sizeLimits: CORNER_SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван угловой модульный, серый",
				description: "Модульный угловой диван 2215 × 2700, длинная сторона уходит слева вперёд.",
				width: 2215,
				height: 700,
				depth: 2700,
				color: "#7a746a",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.SofaCornerOrange,
				family: "sofa-corner",
				sizeLimits: CORNER_SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван угловой, терракотовый",
				description: "Угловой диван 2600 × 1820 с шезлонгом справа, терракотовая ткань.",
				width: 2600,
				height: 910,
				depth: 1820,
				color: "#986226",
			},
			{ upholstery: true, upholsteryColor: "#ba7e30" },
		),
		glb(
			{
				id: FurnitureId.SofaCornerSectional,
				family: "sofa-corner",
				sizeLimits: CORNER_SOFA_LIMITS,
				subGroup: "Гостиная",
				name: "Диван угловой, округлый",
				description: "Угловой диван 2800 × 2350 со скруглёнными спинками, угол слева.",
				width: 2800,
				height: 730,
				depth: 2350,
				color: "#b4a28c",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.ArmchairClassic,
				family: "armchair",
				sizeLimits: ARMCHAIR_LIMITS,
				subGroup: "Гостиная",
				name: "Кресло классическое",
				description: "Классическое кресло 900 × 900 с высокими подлокотниками.",
				width: 900,
				height: 830,
				depth: 900,
				color: "#705d4b",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.ArmchairBeige,
				family: "armchair",
				sizeLimits: ARMCHAIR_LIMITS,
				subGroup: "Гостиная",
				name: "Кресло бежевое",
				description: "Мягкое кресло 800 × 900 в бежевой обивке.",
				width: 800,
				height: 855,
				depth: 900,
				color: "#c1976c",
			},
			// Модель смотрит передом в −Z
			{ turn: 180, upholstery: true },
		),
		glb({
			id: FurnitureId.ArmchairLeather,
			family: "armchair",
			sizeLimits: ARMCHAIR_LIMITS,
			subGroup: "Гостиная",
			name: "Кресло кожаное",
			description: "Низкое кресло 840 × 900 из коричневой кожи.",
			width: 840,
			height: 740,
			depth: 900,
			color: "#683826",
		}),
		glb(
			{
				id: FurnitureId.ArmchairWood,
				family: "armchair",
				sizeLimits: ARMCHAIR_LIMITS,
				subGroup: "Гостиная",
				name: "Кресло с деревянными подлокотниками",
				description: "Кресло 750 × 825 на деревянном каркасе, мягкие сиденье и спинка.",
				width: 750,
				height: 950,
				depth: 825,
				color: "#967f6d",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.ArmchairSoft,
				family: "armchair",
				sizeLimits: ARMCHAIR_LIMITS,
				subGroup: "Гостиная",
				name: "Кресло мягкое, чёрное",
				description: "Широкое мягкое кресло 950 × 920 в чёрной обивке.",
				width: 950,
				height: 815,
				depth: 920,
				color: "#292627",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.BedClassic,
				family: "bed-double",
				sizeLimits: BED_LIMITS,
				subGroup: "Спальня",
				name: "Кровать низкая, светлая",
				description: "Низкая кровать 1470 × 2100 с невысоким изголовьем и светлым бельём.",
				width: 1470,
				height: 545,
				depth: 2100,
				color: "#b5aca9",
			},
			// Модель стоит изголовьем к +X
			{ turn: 90 },
		),
		glb({
			id: FurnitureId.BedKing,
			family: "bed-double",
			sizeLimits: BED_LIMITS,
			subGroup: "Спальня",
			name: "Кровать широкая, низкая",
			description: "Широкая кровать 2100 × 2060 на низком основании с изголовьем.",
			width: 2100,
			height: 735,
			depth: 2060,
			color: "#796659",
		}),
		glb({
			id: FurnitureId.BedLuxury2,
			family: "bed-double",
			sizeLimits: BED_LIMITS,
			subGroup: "Спальня",
			name: "Кровать ореховая",
			description: "Кровать 2100 × 2100 в оттенке ореха с мягким изголовьем.",
			width: 2100,
			height: 1135,
			depth: 2100,
			color: "#90765f",
		}),
		glb(
			{
				id: FurnitureId.BedLuxury3,
				family: "bed-double",
				sizeLimits: BED_LIMITS,
				subGroup: "Спальня",
				name: "Кровать с мягким изголовьем, серая",
				description: "Кровать 1980 × 2100 с серым мягким изголовьем.",
				width: 1980,
				height: 1200,
				depth: 2100,
				color: "#9a968f",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.BedLuxury4,
				family: "bed-double",
				sizeLimits: BED_LIMITS,
				subGroup: "Спальня",
				name: "Кровать с каретной стяжкой",
				description: "Кровать 1980 × 2100 с высоким изголовьем в каретной стяжке.",
				width: 1980,
				height: 1325,
				depth: 2100,
				color: "#8b857c",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.BedPink,
				family: "bed-double",
				sizeLimits: BED_LIMITS,
				subGroup: "Спальня",
				name: "Кровать пудровая",
				description: "Кровать 1550 × 2150 в пудрово-розовой обивке.",
				width: 1550,
				height: 1075,
				depth: 2150,
				color: "#beb3ad",
			},
			{ upholstery: true },
		),
		glb(
			{
				id: FurnitureId.BedTufted,
				family: "bed-double",
				sizeLimits: BED_LIMITS,
				subGroup: "Спальня",
				name: "Кровать с капитоне, серая",
				description: "Кровать 1830 × 2100 с серым изголовьем-капитоне.",
				width: 1830,
				height: 1260,
				depth: 2100,
				color: "#91908d",
			},
			{ upholstery: true },
		),
		glb({
			id: FurnitureId.BedModern,
			family: "bed-double",
			sizeLimits: BED_LIMITS,
			subGroup: "Спальня",
			name: "Кровать современная",
			description: "Кровать 2080 × 2100 с прямым изголовьем, высота 1000.",
			width: 2080,
			height: 1000,
			depth: 2100,
			color: "#958e85",
		}),
		glb(
			{
				id: FurnitureId.BedKidsSofa,
				subGroup: "Детская",
				name: "Кровать детская мягкая",
				description: "Детская кровать 1310 × 1900 с мягкими бортиками.",
				width: 1310,
				height: 1025,
				depth: 1900,
				color: "#a1977e",
			},
			// Модель стоит изголовьем к +X
			{ turn: 90 },
		),
		glb({
			id: FurnitureId.BedKidsWood,
			subGroup: "Детская",
			name: "Кровать детская деревянная",
			description: "Деревянная детская кровать 1395 × 1700 с бортиками.",
			width: 1395,
			height: 800,
			depth: 1700,
			color: "#96766d",
		}),
		glb(
			{
				id: FurnitureId.ChairModern,
				family: "chair",
				subGroup: "Кухня",
				name: "Стул мягкий, серый",
				description: "Мягкий стул 615 × 690 в серой обивке, высота 820.",
				width: 615,
				height: 820,
				depth: 690,
				color: "#777570",
			},
			// Модель смотрит передом в +X
			{ turn: 270, upholstery: true },
		),
		glb({
			id: FurnitureId.ChairWooden,
			family: "chair",
			subGroup: "Кухня",
			name: "Стул деревянный, лак",
			description: "Деревянный стул 585 × 605 под лаком, высота 900.",
			width: 585,
			height: 900,
			depth: 605,
			color: "#ab743f",
		}),
		glb({
			id: FurnitureId.ChairWooden2,
			family: "chair",
			subGroup: "Кухня",
			name: "Стул деревянный, светлый",
			description: "Стул 610 × 650 из светлого дерева, высота 900.",
			width: 610,
			height: 900,
			depth: 650,
			color: "#c0a485",
		}),
		glb({
			id: FurnitureId.ChairWhite,
			family: "chair",
			subGroup: "Кухня",
			name: "Стул белый",
			description: "Стул 450 × 560 со светлым сиденьем, высота 850.",
			width: 450,
			height: 850,
			depth: 560,
			color: "#cac1bc",
		}),
		glb(
			{
				id: FurnitureId.ChairGreen,
				family: "chair",
				subGroup: "Кухня",
				name: "Стул оливковый",
				description: "Мягкий стул 545 × 605 в оливковой обивке, высота 850.",
				width: 545,
				height: 850,
				depth: 605,
				color: "#81856a",
			},
			// Модель повёрнута на 45°: спинкой к −X−Z
			{ turn: 315 },
		),
		glb({
			id: FurnitureId.ChairFoldingWood,
			family: "chair",
			subGroup: "Кухня",
			name: "Стул складной деревянный",
			description: "Деревянный складной стул 575 × 690, высота 800.",
			width: 575,
			height: 800,
			depth: 690,
			color: "#80512d",
		}),
		glb({
			id: FurnitureId.BarStoolWood,
			round: true,
			subGroup: "Кухня",
			name: "Стул барный, светлое дерево",
			description: "Барный стул ⌀ 580 из светлого дерева со спинкой, высота 1000.",
			width: 580,
			height: 1000,
			depth: 580,
			color: "#e7e0d5",
		}),
		glb({
			id: FurnitureId.ChairOfficeLeather,
			family: "chair-office",
			subGroup: "Кабинет",
			name: "Кресло руководителя, кожа",
			description: "Кресло 680 × 665 из коричневой кожи с высокой спинкой, высота 1150.",
			width: 680,
			height: 1150,
			depth: 665,
			color: "#6d4e3d",
		}),
		glb(
			{
				id: FurnitureId.ChairOfficeDystopian,
				family: "chair-office",
				subGroup: "Кабинет",
				name: "Кресло компьютерное футуристичное",
				description: "Чёрное кресло 830 × 840 с массивной спинкой, высота 1200.",
				width: 830,
				height: 1200,
				depth: 840,
				color: "#262626",
			},
			// Модель смотрит передом в +X
			{ turn: 270 },
		),
		glb(
			{
				id: FurnitureId.ChairOffice2,
				family: "chair-office",
				subGroup: "Кабинет",
				name: "Кресло офисное, серое",
				description: "Офисное кресло 725 × 700 в серой обивке, высота 1150.",
				width: 725,
				height: 1150,
				depth: 700,
				color: "#686560",
			},
			// Модель смотрит передом в +X
			{ turn: 270 },
		),
		glb({
			id: FurnitureId.ChairOffice3,
			family: "chair-office",
			subGroup: "Кабинет",
			name: "Кресло офисное мягкое",
			description: "Мягкое офисное кресло 780 × 860, высота 1000.",
			width: 780,
			height: 1000,
			depth: 860,
			color: "#544742",
		}),
		glb({
			id: FurnitureId.DeskModern,
			family: "desk",
			subGroup: "Кабинет",
			name: "Стол письменный современный",
			description: "Широкий стол 1900 × 740 на металлических опорах, высота 720.",
			width: 1900,
			height: 720,
			depth: 740,
			color: "#a2abb4",
		}),
		glb({
			id: FurnitureId.TvStandWood,
			family: "tv-stand",
			subGroup: "Гостиная",
			name: "Тумба под телевизор, тёмное дерево",
			description: "Низкая тумба 1800 × 450 из тёмного дерева, высота 450.",
			width: 1800,
			height: 450,
			depth: 450,
			color: "#54422f",
		}),
		glb({
			id: FurnitureId.SideboardGrey,
			family: "tv-stand",
			subGroup: "Гостиная",
			name: "Буфет серый",
			description: "Буфет 1600 × 450 в графитовом цвете, высота 780.",
			width: 1600,
			height: 780,
			depth: 450,
			color: "#555551",
		}),
		glb({
			id: FurnitureId.PianoAntique,
			subGroup: "Гостиная",
			name: "Пианино антикварное",
			description: "Старинное низкое пианино 1500 × 600 из тёмного дерева, высота 705.",
			width: 1500,
			height: 705,
			depth: 600,
			color: "#2b140e",
		}),
		glb({
			id: FurnitureId.BenchOrnate,
			subGroup: "Гостиная",
			name: "Скамья резная",
			description: "Резная скамья 1300 × 535 из тёмного дерева с мягким сиденьем, высота 560.",
			width: 1300,
			height: 560,
			depth: 535,
			color: "#422318",
		}),
		glb({
			id: FurnitureId.FloorLampGold,
			family: "floor-lamp",
			subGroup: "Гостиная",
			name: "Торшер золотой",
			description: "Торшер высотой 1700 с золотистым плафоном. Декор: свет не даёт.",
			width: 835,
			height: 1700,
			depth: 510,
			color: "#dab798",
		}),
		glb({
			id: FurnitureId.NightstandBedside,
			family: "nightstand",
			subGroup: "Спальня",
			name: "Тумба прикроватная, светлая",
			description: "Светлая тумба 515 × 500 с ящиками, высота 600.",
			width: 515,
			height: 600,
			depth: 500,
			color: "#b7a789",
		}),
		glb({
			id: FurnitureId.NightstandTable,
			family: "nightstand",
			subGroup: "Спальня",
			name: "Тумба-столик",
			description: "Тумба-столик 550 × 520, высота 550.",
			width: 550,
			height: 550,
			depth: 520,
			color: "#807365",
		}),
		glb({
			id: FurnitureId.DresserLow,
			family: "dresser",
			subGroup: "Спальня",
			name: "Комод низкий на ножках",
			description: "Деревянный комод 1200 × 450 на ножках, дверцы и ящики, высота 600.",
			width: 1200,
			height: 600,
			depth: 450,
			color: "#7c6146",
		}),
		glb({
			id: FurnitureId.DressingTableWood,
			subGroup: "Спальня",
			name: "Туалетный столик деревянный",
			description: "Столик 1100 × 625 из светлого дерева, высота 815.",
			width: 1100,
			height: 815,
			depth: 625,
			color: "#ccc1b2",
		}),
		glb({
			id: FurnitureId.BookshelfAntique,
			family: "bookshelf",
			subGroup: "Хранение",
			name: "Шкаф книжный антикварный",
			description: "Книжный шкаф 1800 × 450 из тёмного дерева, высота 1730.",
			width: 1800,
			height: 1730,
			depth: 450,
			color: "#664a37",
		}),
		glb({
			id: FurnitureId.BookshelfLow,
			family: "bookshelf",
			subGroup: "Хранение",
			name: "Стеллаж невысокий, дерево",
			description: "Деревянный стеллаж 1070 × 445 с книгами, высота 1200.",
			width: 1070,
			height: 1200,
			depth: 445,
			color: "#7e5e47",
		}),
		glb({
			id: FurnitureId.WardrobeWood,
			family: "wardrobe",
			subGroup: "Хранение",
			name: "Шкаф платяной, тёмное дерево",
			description: "Шкаф 1900 × 600 из тёмного дерева, высота 2100.",
			width: 1900,
			height: 2100,
			depth: 600,
			color: "#473425",
		}),
		glb({
			id: FurnitureId.StoolKids,
			round: true,
			subGroup: "Детская",
			name: "Табурет детский",
			description: "Яркий детский табурет ⌀ 350, высота 315.",
			width: 350,
			height: 315,
			depth: 350,
			color: "#da8f4e",
		}),
		glb({
			id: FurnitureId.ToyChest,
			subGroup: "Детская",
			name: "Сундук для игрушек",
			description: "Деревянный сундук 800 × 440 с крышкой, высота 465.",
			width: 800,
			height: 465,
			depth: 440,
			color: "#c08340",
		}),
		glb({
			id: FurnitureId.HighChairWood,
			subGroup: "Детская",
			name: "Стульчик для кормления деревянный",
			description: "Деревянный стульчик 680 × 810 со столиком, высота 1000.",
			width: 680,
			height: 1000,
			depth: 810,
			color: "#978361",
		}),
	],
};
